import _defineProperty from "/app/node_modules/next/dist/compiled/@babel/runtime/helpers/esm/defineProperty.js";
import _slicedToArray from "/app/node_modules/next/dist/compiled/@babel/runtime/helpers/esm/slicedToArray.js";
import _objectWithoutProperties from "/app/node_modules/next/dist/compiled/@babel/runtime/helpers/esm/objectWithoutProperties.js";
var _excluded = ["stage", "profileId", "completeStage", "activity", "completeStageStatus", "isPreview"];

function ownKeys(object, enumerableOnly) { var keys = Object.keys(object); if (Object.getOwnPropertySymbols) { var symbols = Object.getOwnPropertySymbols(object); enumerableOnly && (symbols = symbols.filter(function (sym) { return Object.getOwnPropertyDescriptor(object, sym).enumerable; })), keys.push.apply(keys, symbols); } return keys; }

function _objectSpread(target) { for (var i = 1; i < arguments.length; i++) { var source = null != arguments[i] ? arguments[i] : {}; i % 2 ? ownKeys(Object(source), !0).forEach(function (key) { _defineProperty(target, key, source[key]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(target, Object.getOwnPropertyDescriptors(source)) : ownKeys(Object(source)).forEach(function (key) { Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key)); }); } return target; }

import { makeReference } from '@apollo/client';
import { AsyncButton, QuizQuestions, SaveStates } from '@infl-ts/ui-advocate-components';
import { AppAlertsContext } from '@infl-ts/ui-app-alerts';
import { useFocusErrors } from '@infl-ts/util-react-hook-form';
import { QuizTimelineEntries } from '@infl-ts/util-use-challenge-player';
import { useMutationMapper } from '@infl-ts/util-use-mutation-mapper';
import { Alert, AlertTitle, Box, Typography } from '@mui/material';
import * as DE from '@nll/datum/lib/DatumEither';
import * as A from 'fp-ts/Array';
import * as E from 'fp-ts/Either';
import { constFalse, constVoid, flow, pipe } from 'fp-ts/function';
import * as O from 'fp-ts/Option';
import * as R from 'fp-ts/Record';
import * as S from 'fp-ts/Semigroup';
import { useContext, useEffect, useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import { Translation } from 'react-i18next';
import { getLastTimelineEntry } from '../corporate-approval';
import StageLayout from '../stage-layout';
import { useSubmitQuiz } from './mutations';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";

var formatQuestionDefaults = function formatQuestionDefaults(questions) {
  return R.fromFoldableMap(S.last(), A.Foldable)(questions, function (q) {
    return [q.id, ''];
  });
};

var QuizResult = function QuizResult(_ref) {
  var correctAnswers = _ref.correctAnswers,
      totalQuestions = _ref.totalQuestions;
  return /*#__PURE__*/_jsx(Typography, {
    variant: "h3",
    sx: {
      ml: 2
    },
    "data-element": "Typography",
    "data-component": "QuizResult",
    "data-source-file": "index.tsx",
    children: /*#__PURE__*/_jsx(Translation, {
      "data-element": "Translation",
      "data-component": "QuizResult",
      "data-source-file": "index.tsx",
      children: function children(t) {
        return t('Score: {{correctAnswers}} / {{totalQuestions}}', {
          correctAnswers: correctAnswers,
          totalQuestions: totalQuestions
        });
      }
    })
  });
};

var PureQuizStage = function PureQuizStage(_ref2) {
  var _stage$questions$leng, _stage$questions;

  var stage = _ref2.stage,
      questionForm = _ref2.questionForm,
      onSubmit = _ref2.onSubmit,
      quizResults = _ref2.quizResults,
      mutationValue = _ref2.mutationValue,
      questionRef = _ref2.questionRef,
      prospect = _ref2.prospect,
      alreadyAnswered = _ref2.alreadyAnswered;
  return /*#__PURE__*/_jsx(StageLayout, {
    hidePoints: true,
    stage: stage,
    prospect: prospect,
    showPublicResponseMessage: !alreadyAnswered,
    actions: /*#__PURE__*/_jsxs(Box, {
      sx: {
        display: 'flex',
        alignItems: 'center'
      },
      children: [/*#__PURE__*/_jsx(AsyncButton, {
        sx: {
          mx: 1
        },
        status: mutationValue,
        onClick: onSubmit,
        text: ['Submitting...', 'Next', 'Next', quizResults || alreadyAnswered ? 'Continue' : stage.completionText || 'Submit']
      }), quizResults && /*#__PURE__*/_jsx(QuizResult, {
        correctAnswers: quizResults.correctQuestions,
        totalQuestions: (_stage$questions$leng = (_stage$questions = stage.questions) === null || _stage$questions === void 0 ? void 0 : _stage$questions.length) !== null && _stage$questions$leng !== void 0 ? _stage$questions$leng : 0
      })]
    }),
    "data-element": "StageLayout",
    "data-component": "PureQuizStage",
    "data-source-file": "index.tsx",
    children: alreadyAnswered ? /*#__PURE__*/_jsx(Translation, {
      children: function children(t) {
        return /*#__PURE__*/_jsx(Alert, {
          sx: {
            mt: 4,
            mx: 3
          },
          severity: "info",
          children: /*#__PURE__*/_jsx(AlertTitle, {
            sx: {
              fontWeight: 'bold'
            },
            children: t('You have already completed this quiz')
          })
        });
      }
    }) : stage.questions ? /*#__PURE__*/_jsx(QuizQuestions, {
      quizQuestions: stage.questions,
      responseResults: (quizResults === null || quizResults === void 0 ? void 0 : quizResults.responseResults) || [],
      quizForm: questionForm,
      questionRef: questionRef
    }) : null
  });
};

var useProps = function useProps(_ref3) {
  var stage = _ref3.stage,
      profileId = _ref3.profileId,
      completeStage = _ref3.completeStage,
      stageActivity = _ref3.activity,
      completeStageStatus = _ref3.completeStageStatus,
      isPreview = _ref3.isPreview,
      props = _objectWithoutProperties(_ref3, _excluded);

  var _useContext = useContext(AppAlertsContext),
      showAlert = _useContext.showAlert;

  var _useState = useState(null),
      quizResults = _useState[0],
      setQuizResults = _useState[1];

  var alreadyAnswered = useMemo(function () {
    return pipe(stageActivity, getLastTimelineEntry, O.chain(O.fromPredicate(QuizTimelineEntries.is)), O.match(constFalse, function (entry) {
      var _entry$stage;

      return ((_entry$stage = entry.stage) === null || _entry$stage === void 0 ? void 0 : _entry$stage.id) === stage.id;
    }));
  }, [stageActivity]);

  var _useSubmitQuiz = useSubmitQuiz({
    onCompleted: E.match(function (e) {
      console.error(e);
      showAlert({
        severity: 'error',
        message: 'Something went wrong',
        duration: 'medium'
      });
    }, function (_ref4) {
      var submitQuiz = _ref4.submitQuiz;

      if (submitQuiz.__typename === 'SubmitQuizResult') {
        setQuizResults(submitQuiz);
      } else {
        showAlert({
          severity: 'error',
          message: submitQuiz.errors[0].message,
          duration: 'medium'
        });
      }
    }),
    update: function update(cache, data) {
      return pipe(data, E.match(constVoid, function (_ref5) {
        var submitQuiz = _ref5.submitQuiz;
        submitQuiz.__typename == 'SubmitQuizResult' && cache.modify({
          id: cache.identify(makeReference("Profile:".concat(profileId))),
          fields: {
            currentPoints: function currentPoints(current) {
              var _submitQuiz$pointsEar;

              return current + ((_submitQuiz$pointsEar = submitQuiz.pointsEarned) !== null && _submitQuiz$pointsEar !== void 0 ? _submitQuiz$pointsEar : 0);
            }
          }
        });
      }));
    }
  }),
      _useSubmitQuiz2 = _slicedToArray(_useSubmitQuiz, 2),
      onSubmitQuiz = _useSubmitQuiz2[0],
      submitQuizStatus = _useSubmitQuiz2[1];

  useEffect(function () {
    setQuizResults(null);
  }, [stage]);
  var questionForm = useForm({
    defaultValues: stage.questions ? formatQuestionDefaults(stage.questions) : undefined
  });
  var Saving = SaveStates.Saving,
      Default = SaveStates.Default,
      Error = SaveStates.Error;
  var stageMutationValue = useMutationMapper(completeStageStatus, function (_ref6) {
    var completeStage = _ref6.completeStage;
    return completeStage.__typename === 'CompleteStageErrors' ? E.left(null) : E.right(null);
  }, [Saving, Default, Error, Default]);
  var quizMutationValue = useMutationMapper(submitQuizStatus, function (_ref7) {
    var submitQuiz = _ref7.submitQuiz;
    return submitQuiz.__typename === 'SubmitQuizResult' ? E.left(null) : E.right(null);
  }, [Saving, Default, Error, Default]);

  var _useFocusErrors = useFocusErrors(),
      questionRef = _useFocusErrors.questionRef,
      focusOnFirstError = _useFocusErrors.focusOnFirstError;

  var handleCompleteStage = function handleCompleteStage() {
    return completeStage({
      quizInput: {
        stageID: stage.id,
        activityID: stageActivity === null || stageActivity === void 0 ? void 0 : stageActivity.id
      }
    });
  };

  var handleSubmitQuizAnswers = questionForm.handleSubmit(function (data) {
    void onSubmitQuiz({
      variables: {
        input: {
          stageId: stage.id,
          responses: Object.entries(data).map(function (_ref8) {
            var _ref9 = _slicedToArray(_ref8, 2),
                questionId = _ref9[0],
                radioResponse = _ref9[1];

            return {
              questionId: questionId,
              radioResponse: radioResponse
            };
          })
        }
      }
    });
  }, focusOnFirstError);
  return _objectSpread({
    stage: stage,
    onSubmit: isPreview ? constVoid : quizResults || alreadyAnswered ? handleCompleteStage : handleSubmitQuizAnswers,
    alreadyAnswered: alreadyAnswered,
    mutationValue: DE.isPending(submitQuizStatus) ? quizMutationValue : stageMutationValue,
    questionForm: questionForm,
    questionRef: questionRef,
    quizResults: DE.isSuccess(completeStageStatus) || DE.isInitial(completeStageStatus) ? quizResults : null
  }, props);
};

export default Object.assign(flow(useProps, PureQuizStage), {
  displayName: 'QuizStage'
});